import { api } from './api';

export interface BiReportOwner {
  id: string;
  name: string;
}

export interface BiReport {
  id: string;
  reportName: string;
  groupNameSamasc: string;
  groupId: string;
  embededId: string;
  embededUrl: string;
  id_power_bi_configs: string | null;
  status: boolean;
  description: string | null;
  refresh_frequency: string | null;
  glossary: string | null;
  display_order: number;
  summary: string | null;
  card_color: string | null;
  card_icon: string | null;
  owner: BiReportOwner | null;
  permissions: string[];
  roles: string[];
  isFavorite: boolean;
  lastViewedAt: string | null;
  updated_at: string;
}

export interface BiEmbedToken {
  accessToken: string;
  expiry: string;
}

export interface BiView {
  id: string;
  name: string;
  bookmark: string;
  embedId: string;
  public: boolean;
  access_id: string;
}

export type BiCommentType = 'duvida' | 'sugestao' | 'erro';
export type BiCommentStatus = 'aberto' | 'resolvido';

export interface BiComment {
  id: string;
  power_bi_report_id: string;
  parent_id: string | null;
  page_name: string | null;
  type: BiCommentType;
  status: BiCommentStatus;
  content: string;
  author: { id: string; name: string; image: string | null } | null;
  created_at: string;
  updated_at: string;
  replies: BiComment[];
}

export interface BiConfig {
  id: string;
  authenticationMode: string;
  authorityUri: string;
  scope: string;
  apiUrl: string;
  clientId: string;
  workspaceId: string;
  reportId: string;
  pbiUsername: string;
  pbiPassword: string;
  clientSecret: string;
  tenantId: string;
}

export type BiConfigInput = Omit<BiConfig, 'id'>;

export interface BiPermission {
  id: string;
  name: string;
  identification: string;
  description: string;
}

export interface BiUserOption {
  id: string;
  name: string;
}

export interface BiReportInput {
  reportName: string;
  groupNameSamasc: string;
  groupName: string;
  groupId: string;
  embededId: string;
  embededUrl: string;
  id_power_bi_configs: string;
  description: string;
  refresh_frequency: string;
  glossary: string;
  owner_id: string | null;
  display_order: number;
  status: boolean;
  summary: string;
  card_color: string | null;
  card_icon: string | null;
}

export interface BiGroup {
  id: string;
  name: string;
  color: string;
  icon: string;
  display_order: number;
}

export type BiGroupInput = Pick<BiGroup, 'name'> &
  Partial<Pick<BiGroup, 'color' | 'icon' | 'display_order'>>;

export function biErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;

  return typeof message === 'string' && message.trim() ? message : fallback;
}

export const biService = {
  async listCatalog(): Promise<BiReport[]> {
    const response = await api.get<BiReport[]>('/power-bi-reports/catalog');

    return Array.isArray(response.data) ? response.data : [];
  },

  async getEmbedToken(report: BiReport): Promise<BiEmbedToken> {
    const response = await api.get<BiEmbedToken>('/get-embed-token', {
      params: {
        reportId: report.embededId,
        workspaceId: report.groupId,
        configId: report.id_power_bi_configs,
      },
    });

    return response.data;
  },

  async registerAccess(reportId: string, pageName?: string): Promise<void> {
    await api.post('/power-bi-access-logs', {
      power_bi_report_id: reportId,
      page_name: pageName || null,
    });
  },

  async addFavorite(reportId: string): Promise<void> {
    await api.post('/power-bi-favorites', { power_bi_report_id: reportId });
  },

  async removeFavorite(reportId: string): Promise<void> {
    await api.delete('/power-bi-favorites', {
      params: { power_bi_report_id: reportId },
    });
  },

  async listViews(accessId: string, embedId: string): Promise<BiView[]> {
    const response = await api.get<BiView[]>('/power-bi-views/show', {
      params: { access_id: accessId, embedId },
    });

    return Array.isArray(response.data) ? response.data : [];
  },

  async createView(view: Omit<BiView, 'id'>): Promise<BiView> {
    const response = await api.post<BiView>('/power-bi-views', view);

    return response.data;
  },

  async renameView(id: string, name: string): Promise<BiView> {
    const response = await api.put<BiView>(
      '/power-bi-views',
      { name },
      { params: { id } },
    );

    return response.data;
  },

  async deleteView(id: string): Promise<void> {
    await api.delete('/power-bi-views', { params: { id } });
  },

  async listComments(reportId: string): Promise<BiComment[]> {
    const response = await api.get<BiComment[]>('/power-bi-comments', {
      params: { power_bi_report_id: reportId },
    });

    return Array.isArray(response.data) ? response.data : [];
  },

  async createComment(data: {
    power_bi_report_id: string;
    content: string;
    type?: BiCommentType;
    parent_id?: string | null;
    page_name?: string | null;
  }): Promise<BiComment> {
    const response = await api.post<BiComment>('/power-bi-comments', data);

    return response.data;
  },

  async updateCommentStatus(
    id: string,
    status: BiCommentStatus,
  ): Promise<BiComment> {
    const response = await api.put<BiComment>(
      '/power-bi-comments',
      { status },
      { params: { id } },
    );

    return response.data;
  },

  // --- Administração ---

  async createReport(data: BiReportInput): Promise<{ id: string }> {
    const response = await api.post<{ id: string }>('/power-bi-reports', data);

    return response.data;
  },

  async updateReport(
    id: string,
    data: Partial<BiReportInput>,
  ): Promise<{ id: string }> {
    const response = await api.put<{ id: string }>('/power-bi-reports', data, {
      params: { id },
    });

    return response.data;
  },

  async syncReportPermissions(
    reportId: string,
    permissionsIds: string[],
  ): Promise<void> {
    await api.put('/power-bi-reports-of-permissions/sync', {
      power_bi_report_id: reportId,
      permissions_ids: permissionsIds,
    });
  },

  async listReportPermissionIds(reportId: string): Promise<string[]> {
    const response = await api.get<{ permission_id: string }[]>(
      '/power-bi-reports-of-permissions/show',
      { params: { power_bi_report_id: reportId } },
    );
    const links = Array.isArray(response.data) ? response.data : [];

    return links.map(link => link.permission_id);
  },

  async listPermissions(): Promise<BiPermission[]> {
    const response = await api.get<BiPermission[]>('/permissions');

    return Array.isArray(response.data) ? response.data : [];
  },

  async listUsers(): Promise<BiUserOption[]> {
    const response = await api.get<BiUserOption[]>('/accesses', {
      params: { status: true },
    });
    const users = Array.isArray(response.data) ? response.data : [];

    return users
      .map(user => ({ id: user.id, name: user.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  },

  async listGroups(): Promise<BiGroup[]> {
    const response = await api.get<BiGroup[]>('/power-bi-groups');

    return Array.isArray(response.data) ? response.data : [];
  },

  async upsertGroup(data: BiGroupInput): Promise<BiGroup> {
    const response = await api.put<BiGroup>('/power-bi-groups', data);

    return response.data;
  },

  async listConfigs(): Promise<BiConfig[]> {
    const response = await api.get<BiConfig[]>('/power-bi-configs');

    return Array.isArray(response.data) ? response.data : [];
  },

  async createConfig(data: BiConfigInput): Promise<void> {
    await api.post('/power-bi-configs', data);
  },

  async updateConfig(id: string, data: Partial<BiConfigInput>): Promise<void> {
    await api.put('/power-bi-configs', data, { params: { id } });
  },
};
